import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { HttpBackendService } from 'src/app/core/backend-services/http/http.backend-service';
import { GameMessageInterface } from 'src/app/core/interfaces/game-message/game-message.interface';

export interface ArchivedGamePlayer {
  userID: number;
  username: string;
  profilePicture?: string;
  smallProfilePicture?: string;
}

export interface ArchivedGameRound {
  id: number;
  uuid: string;
  numPlayers: number;
  players: ArchivedGamePlayer[];
  messages: GameMessageInterface[];
  createdAt: string;
  updatedAt: string;
}

export interface ArchivedGame {
  id: number;
  uuid: string;
  numPlayers: number;
  players: ArchivedGamePlayer[];
  isNPCGame: boolean;
  winnersIdx: number[];
  losersIdx: number[];
  points: number[];
  createdAt: string;
  updatedAt: string;
  startedAt?: string | null; // with endedAt: real duration (null while unknown)
  endedAt?: string | null;
  rounds?: ArchivedGameRound[];
}

export interface ArchivedGamesPagination {
  total: number;
  count: number;
  limit: number;
  offset: number;
  hasMore: boolean;
}

export interface ArchivedGamesResponse {
  games: ArchivedGame[];
  pagination: ArchivedGamesPagination;
  filteredByUserID?: number;
  filteredByUsername?: string;
}

export interface ArchivedGamesParams {
  limit?: number; // 1-100, default: 10
  offset?: number; // default: 0
  userID?: number; // Filter nach User-ID (optional)
  username?: string; // Filter nach Username (optional)
  gameType?: '2_player' | '3_player' | '4_player' | 'all'; // Filter nach Anzahl Spieler (default: 'all')
  period?: 'today' | 'this_week' | 'this_month' | 'this_year' | 'all_time'; // Filter nach Zeitraum (default: 'all_time')
  result?: 'won' | 'lost' | 'all'; // Filter nach Gewonnen/Verloren (default: 'all', funktioniert nur mit userID)
  isNPC?: boolean | 'all'; // Filter nach NPC/Human Spielen (default: 'all')
  skipCount?: boolean; // Skip total count query for better performance
}

@Injectable({
  providedIn: 'root',
})
export class ArchivedGamesBackendService {
  private readonly BASE_ADDRESS = '/game/archived-game';

  /**
   * @description constructor of ArchivedGamesBackendService
   * @param {HttpBackendService} http - http backend service
   */
  constructor(private http: HttpBackendService) {}

  /**
   * @description gets an archived game
   * @param {string} uuid
   * @returns {Promise<ArchivedGame>}
   */
  public get(uuid: string): Promise<ArchivedGame> {
    return firstValueFrom(
      this.http.get<ArchivedGame>(`${this.BASE_ADDRESS}/${uuid}`)
    );
  }

  /**
   * @description gets archived games with optional filtering and pagination
   * @param {ArchivedGamesParams} params - query parameters for filtering and pagination
   * @returns {Promise<ArchivedGamesResponse>}
   */
  public getArchivedGames(
    params: ArchivedGamesParams = {}
  ): Promise<ArchivedGamesResponse> {
    // Validate and set defaults
    const queryParams: ArchivedGamesParams = {
      limit: Math.min(100, Math.max(1, params.limit || 10)),
      offset: Math.max(0, params.offset || 0),
      ...params,
    };

    // Build query string
    const queryString = this.buildQueryString(queryParams);
    const url = queryString
      ? `${this.BASE_ADDRESS}/list?${queryString}`
      : `${this.BASE_ADDRESS}/list`;

    return firstValueFrom(this.http.get<ArchivedGamesResponse>(url));
  }

  /**
   * @description gets recent archived games with default pagination
   * @param {number} limit - number of games to fetch (default: 10, max: 50)
   * @returns {Promise<ArchivedGamesResponse>}
   */
  public getRecentGames(limit: number = 10): Promise<ArchivedGamesResponse> {
    return this.getArchivedGames({ limit: Math.min(50, Math.max(1, limit)), skipCount: true });
  }

  /**
   * @description gets archived games for a specific user
   * @param {number} userID - the user ID to filter by
   * @param {number} limit - number of games to fetch (default: 20)
   * @param {number} offset - offset for pagination (default: 0)
   * @returns {Promise<ArchivedGamesResponse>}
   */
  public getUserGames(
    userID: number,
    limit: number = 20,
    offset: number = 0
  ): Promise<ArchivedGamesResponse> {
    return this.getArchivedGames({ userID, limit, offset });
  }

  /**
   * @description gets archived games for a specific username
   * @param {string} username - the username to filter by
   * @param {number} limit - number of games to fetch (default: 20)
   * @param {number} offset - offset for pagination (default: 0)
   * @returns {Promise<ArchivedGamesResponse>}
   */
  public getUserGamesByUsername(
    username: string,
    limit: number = 20,
    offset: number = 0
  ): Promise<ArchivedGamesResponse> {
    return this.getArchivedGames({ username, limit, offset });
  }

  /**
   * @description builds query string from parameters
   * @param {ArchivedGamesParams} params - parameters object
   * @returns {string} query string
   */
  private buildQueryString(params: ArchivedGamesParams): string {
    const queryParts: string[] = [];

    if (params.limit !== undefined) {
      queryParts.push(`limit=${params.limit}`);
    }
    if (params.offset !== undefined) {
      queryParts.push(`offset=${params.offset}`);
    }
    if (params.userID !== undefined) {
      queryParts.push(`userID=${params.userID}`);
    }
    if (params.username !== undefined) {
      queryParts.push(`username=${encodeURIComponent(params.username)}`);
    }
    if (params.gameType !== undefined) {
      queryParts.push(`gameType=${params.gameType}`);
    }
    if (params.period !== undefined) {
      queryParts.push(`period=${params.period}`);
    }
    if (params.result !== undefined) {
      queryParts.push(`result=${params.result}`);
    }
    if (params.isNPC !== undefined && params.isNPC !== 'all') {
      queryParts.push(`isNPC=${params.isNPC}`);
    }
    if (params.skipCount) {
      queryParts.push('skipCount=true');
    }

    return queryParts.join('&');
  }
}
